import { Disclosure } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { FC } from 'react';

const accreditationDetailsFaqs = [
  {
    question: 'Who can get accredited?',
    answer: (
      <div>
        <p>
          Individuals, or entities set up as HUFs, Trusts, Partnership Firms, or Body Corporates,
          that qualify the accreditation criteria are eligible for accreditation.
        </p>
      </div>
    ),
  },
  {
    question: 'What is the criteria for accreditation?',
    answer: (
      <div>
        <h3 className="mt-2 font-semibold">Individuals, HUFs, and Family Trusts:</h3>
        <ul className="list-disc pl-5 mt-2">
          <li>Annual income of ₹2 crore or more, OR</li>
          <li>
            Annual income of ₹1 crore or more and net worth of at least ₹5 crore (with at least ₹2.5
            crore in financial assets), OR
          </li>
          <li>Net worth of at least ₹7.5 crore (with at least ₹3.75 crore in financial assets).</li>
        </ul>

        <h3 className="mt-4 font-semibold">
          Partnership Firms (set up under the Indian Partnership Act, 1932):
        </h3>
        <ul className="list-disc pl-5 mt-2">
          <li>Each partner independently meets the criteria for accreditation</li>
        </ul>

        <h3 className="mt-4 font-semibold">Trusts (other than Family Trusts):</h3>
        <ul className="list-disc pl-5 mt-2">
          <li>Net worth of at least ₹50 crore</li>
        </ul>

        <h3 className="mt-4 font-semibold">Body Corporates:</h3>
        <ul className="list-disc pl-5 mt-2">
          <li>Net worth of at least ₹50 crore</li>
        </ul>
      </div>
    ),
  },
  {
    question: 'Who processes the accreditation application?',
    answer: (
      <div>
        <p>
          The accreditation application is processed by SEBI-recognized accreditation agencies, CDSL
          Ventures Limited (CVL) and NSDL Database Management Limited (NDML), both of which are
          registered as KYC Registration Agencies (KRAs). These agencies are responsible for
          granting certificates of accreditation to eligible investors.
        </p>
      </div>
    ),
  },
  {
    question: 'Can there be joint accreditation/joint applicants for accreditation?',
    answer: (
      <div>
        <p>Joint accreditation is permitted. The eligibility will be computed as follows:</p>
        <ul className="list-disc pl-5 mt-2">
          <li>
            <strong>With Spouse</strong> – The combined annual income and/or net worth of the
            applicant, along with their spouse, will be considered for eligibility.
          </li>
          <li>
            <strong>With Parents/Children</strong> – The eligibility of any individual applicant
            will be assessed, and a joint accreditation certificate may then be granted.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: 'What is the cost involved?',
    answer: (
      <div>
        <p>
          The costs mentioned below include the certificate issuance fee, as well as the service and
          processing fee charged by the Accreditation Agencies.
        </p>

        <h3 className="mt-4 font-semibold">
          For Individual Investors, Family Trusts, HUFs, and Sole Proprietorships:
        </h3>
        <ul className="list-disc pl-5 mt-2">
          <li>2-year certificate: INR 11,000 to INR 15,000</li>
          <li>3-year certificate: INR 16,000 and INR 20,000</li>
        </ul>

        <h3 className="mt-4 font-semibold">For Partnership Firms:</h3>
        <ul className="list-disc pl-5 mt-2">
          <li>2-year certificate: INR 16,000 to INR 20,000</li>
          <li>3-year certificate: INR 27,000 to INR 31,000</li>
        </ul>

        <h3 className="mt-4 font-semibold">
          For Trusts (other than Family Trusts) and Body Corporates:
        </h3>
        <ul className="list-disc pl-5 mt-2">
          <li>2-year certificate: INR 22,000 – INR 26,000</li>
          <li>3-year certificate: INR 38,000 – INR 41,000</li>
        </ul>
      </div>
    ),
  },
  {
    question: 'What are the timelines for accreditation?',
    answer: (
      <div>
        <p>
          After submission of the application with the required documents, the Accreditation
          Agencies process it (and issue a certificate) within 5 business days.
        </p>
      </div>
    ),
  },
  {
    question: 'What is the validity of the accreditation certificate?',
    answer: (
      <div>
        <ul className="list-disc pl-5">
          <li>
            If the applicant meets the eligibility criteria for the preceding one financial year,
            the accreditation certificate will be valid for 2 years from the date of issuance.
          </li>
          <li>
            If the applicant meets the eligibility criteria for the preceding two financial years,
            the accreditation certificate will be valid for 3 years from the date of issuance.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: "What if I don't want to get accredited?",
    answer: (
      <div>
        <p>
          If you choose not to get accredited, you can still invest in regular investment products
          (mutual funds, listed equity, etc.). However, you will not be able to:
        </p>
        <ul className="list-disc pl-5 mt-2">
          <li>
            <strong>Invest in Angel Funds:</strong> SEBI's 2025 amendments restrict investment in
            Angel Funds exclusively to accredited investors, i.e, only accredited investors can
            participate in the early-stage investment ecosystem
          </li>
          <li>Participate in co-investment schemes of Category I & II AIFs.</li>
          <li>
            Access regulatory relaxations (such as exemptions from certain investor thresholds and
            SEBI norms) that give accredited investors more flexibility and choice in structuring
            their investments.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: 'Is my accreditation certificate AIF-specific or valid across all AIFs?',
    answer: (
      <div>
        <p>
          Accreditation certificates are applicant-specific and not AIF-specific, i.e., they are
          valid across all AIFs during their validity period. Separate accreditation need not be
          applied for each AIF invested in.
        </p>
      </div>
    ),
  },
  {
    question: 'My accreditation certificate is about to expire. How do I extend its validity?',
    answer: (
      <div>
        <p>
          Once the certificate validity ends, the applicant must reapply with updated documents (as
          per chosen eligibility) to the Accreditation Agency (CVL or NDML) to obtain a new
          certificate.
        </p>
      </div>
    ),
  },
  {
    question: 'My accreditation certificate has expired. Can I renew it?',
    answer: (
      <div>
        <p>
          Once the certificate validity ends, the applicant must reapply with updated documents (as
          per chosen eligibility) to the Accreditation Agency (CVL or NDML) to obtain a new
          certificate.
        </p>
      </div>
    ),
  },
  {
    question: 'Which other SEBI regulated products can I access after getting accredited?',
    answer: (
      <div>
        <p className="mb-4">
          As an accredited investor, you gain access to various SEBI regulated products with relaxed
          ticket sizes and additional investment opportunities. Here's a comprehensive overview:
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
                  Product / Particulars
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">SIF</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
                  Angel Fund
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">PMS</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">AIF</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">SSF</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">LVF</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">
                  Minimum ticket size (INR)
                </td>
                <td className="border border-gray-300 px-4 py-2">10 Lakhs</td>
                <td className="border border-gray-300 px-4 py-2">Presently 25 Lakhs*</td>
                <td className="border border-gray-300 px-4 py-2">50 Lakhs</td>
                <td className="border border-gray-300 px-4 py-2">1 Crore</td>
                <td className="border border-gray-300 px-4 py-2">10 Crore</td>
                <td className="border border-gray-300 px-4 py-2">70 Crore</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">
                  Investment avenues
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  Equity, debt, REITs, derivatives (F&O)
                </td>
                <td className="border border-gray-300 px-4 py-2">Start-ups</td>
                <td className="border border-gray-300 px-4 py-2">
                  <strong>Discretionary PMS:</strong> Listed securities, money market instruments,
                  Mutual Fund units
                  <br />
                  <strong>Non-Discretionary PMS:</strong> Up to 25% in unlisted securities
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  <strong>Category 1 & 2:</strong> Major thrust towards unlisted securities
                  <br />
                  <strong>Category 3:</strong> Diverse trading strategies, 2X leverage allowed
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  Stressed assets - SRs issued by ARCs, stressed loans**
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  In line with AIF category with additional flexibilities
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">
                  Relaxations to AIs (Accredited Investors)
                </td>
                <td className="border border-gray-300 px-4 py-2">No ticket size to AIs</td>
                <td className="border border-gray-300 px-4 py-2">
                  After amendment, only AIs can participate*
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  No ticket size to AIs. Up to 100% in unlisted securities
                </td>
                <td className="border border-gray-300 px-4 py-2">No ticket size to AIs</td>
                <td className="border border-gray-300 px-4 py-2">
                  Ticket size reduced to 5 Crore for AIs
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  Only AIs can participate. Pari Passu not applicable
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>
            <strong>Footnotes:</strong>
          </p>
          <p>* Board has approved the proposal on AI mandate, yet to be notified.</p>
          <p>** Acquisition of Bank & NBFC loans yet to be enabled by RBI.</p>
        </div>
      </div>
    ),
  },
  {
    question:
      'Is there any separate criteria for foreign residents who hold assets in foreign currency?',
    answer: (
      <div>
        <p>
          No, the criteria for accreditation remains the same for foreign nationals and foreign
          residents. Currently, SEBI's framework specifies eligibility in terms of Indian Rupees
          (INR). While applicants may maintain assets in foreign currencies, they need to be
          converted into INR (based on prevailing exchange rates) and substantiated with supporting
          documents to demonstrate compliance with the income/net worth criteria.
        </p>
      </div>
    ),
  },
];

const AccreditationFAQs: FC<{ defaultOpen?: boolean }> = ({ defaultOpen = false }) => {
  return (
    <Disclosure as="div" key={'accreditation-faqs'} defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <dt>
            <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
              <span className={classNames('font-bold text-black', open && 'underline')}>FAQs</span>
              <span className="ml-6 flex h-7 items-center self-center">
                {open ? (
                  <MinusIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <PlusIcon className="h-6 w-6" aria-hidden="true" />
                )}
              </span>
            </Disclosure.Button>
          </dt>
          <Disclosure.Panel as="dd" className="mt-2 pr-12">
            {accreditationDetailsFaqs.map(faq => (
              <Disclosure as="div" key={faq.question} className="pt-6">
                {({ open }) => (
                  <>
                    <dt id={faq.question}>
                      <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                        <span className="text-base font-semibold leading-7">{faq.question}</span>
                        <span className="ml-6 flex h-7 items-center">
                          {open ? (
                            <MinusIcon className="h-6 w-6" aria-hidden="true" />
                          ) : (
                            <PlusIcon className="h-6 w-6" aria-hidden="true" />
                          )}
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel as="dd" className="mt-2 pr-12">
                      <div className="text-base leading-7 text-gray-600">{faq.answer}</div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export { accreditationDetailsFaqs, AccreditationFAQs };
